import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAnalytics } from '@/hooks/useAnalytics';
import { 
  LineChart, 
  Line, 
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { ArrowLeft, Eye, MousePointer, FileText, Clock } from 'lucide-react';

export default function Analytics() {
  const navigate = useNavigate();
  const { data, isLoading } = useAnalytics();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96 mb-8" />
        </div>
      </div>
    );
  }

  const conversionRate = data?.summary.totalViews > 0
    ? ((data.summary.totalClicks / data.summary.totalViews) * 100).toFixed(2)
    : '0.00';

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/automation')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Performance metrics and insights
            </p>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Views
              </CardTitle>
              <Eye className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {data?.summary.totalViews.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                All-time page views
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Affiliate Clicks
              </CardTitle>
              <MousePointer className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {data?.summary.totalClicks.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total conversions
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Conversion Rate
              </CardTitle>
              <FileText className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {conversionRate}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Click-through rate
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Avg Time on Page
              </CardTitle>
              <Clock className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {data?.summary.avgTimeOnPage}s
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Average engagement
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Top Articles Table */}
        <Card className="border-border/40 bg-card/50 mb-8">
          <CardHeader>
            <CardTitle>Top 10 Articles (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                      Article
                    </th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                      Tokens
                    </th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">
                      Views
                    </th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">
                      Unique
                    </th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">
                      Clicks
                    </th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">
                      CTR
                    </th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">
                      Avg Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.topArticles.map((article, index) => (
                    <tr 
                      key={article.articleId} 
                      className="border-b border-border/20 hover:bg-muted/20 transition-colors"
                    >
                      <td className="p-3">
                        <a 
                          href={`/articles/${article.slug}`}
                          className="hover:text-primary transition-colors font-medium"
                        >
                          {article.headline}
                        </a>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          {article.tokens?.slice(0, 3).map((token) => (
                            <span 
                              key={token}
                              className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium"
                            >
                              {token}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="text-right p-3 font-medium">
                        {article.views.toLocaleString()}
                      </td>
                      <td className="text-right p-3 text-muted-foreground">
                        {article.uniqueViews.toLocaleString()}
                      </td>
                      <td className="text-right p-3 font-medium">
                        {article.clicks.toLocaleString()}
                      </td>
                      <td className="text-right p-3 text-primary font-medium">
                        {article.views > 0 
                          ? ((article.clicks / article.views) * 100).toFixed(1)
                          : '0.0'
                        }%
                      </td>
                      <td className="text-right p-3 text-muted-foreground">
                        {article.avgTimeOnPage}s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Views & Clicks Trend */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader>
              <CardTitle>Daily Views & Clicks (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.dailyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={false}
                    name="Views"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="clicks" 
                    stroke="hsl(var(--accent))" 
                    strokeWidth={2}
                    dot={false}
                    name="Clicks"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Articles Bar Chart */}
          <Card className="border-border/40 bg-card/50">
            <CardHeader>
              <CardTitle>Top Articles by Views</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={data?.topArticles.slice(0, 5).map(a => ({
                    name: a.headline.length > 30 
                      ? a.headline.substring(0, 30) + '...' 
                      : a.headline,
                    views: a.views,
                    clicks: a.clicks
                  }))}
                  layout="horizontal"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="hsl(var(--muted-foreground))"
                    width={150}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="views" fill="hsl(var(--primary))" name="Views" />
                  <Bar dataKey="clicks" fill="hsl(var(--accent))" name="Clicks" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Auto-refresh Info */}
        <div className="text-sm text-muted-foreground text-center">
          Dashboard auto-refreshes every minute • Last updated: {new Date().toLocaleTimeString('en-AU')}
        </div>
      </div>
    </div>
  );
}
